// src/App.js
import React from 'react';
import { BrowserRouter as Router, Route, Routes, Link as RouterLink, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import OffersPage from './OffersPage';
import SpinPage from './SpinPage';
import ProfilePage from './ProfilePage';
import InventoryPage from './InventoryPage';
import TermsOfUsePage from './TermsOfUsePage';
import MessagesPage from './MessagesPage'; // <-- Import MessagesPage
import PrivacyPolicyPage from './PrivacyPolicyPage'; // Assuming you have this component
import LandingPage from './LandingPage';
import FaqPage from './FaqPage';
import AdminLayout from './admin/AdminLayout';
import AdminRoute from './AdminRoute';
import UserManagementPage from './admin/UserManagmentPage'; // Corrected spelling
import ItemManagementPage from './admin/ItemManagementPage';
import FeaturedItemManagementPage from './admin/FeaturedItemManagementPage';
import ReferralHandler from './ReferralHandler'; // <-- Import the new component
import NotFoundPage from './NotFoundPage';
// --- Material UI Imports ---
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Link from '@mui/material/Link';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import InventoryIcon from '@mui/icons-material/Inventory';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'; // <-- Import Admin icon
import MailOutlineIcon from '@mui/icons-material/MailOutline'; // <-- Import Mail icon
import Divider from '@mui/material/Divider';
import { alpha } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
// import DialogContentText from '@mui/material/DialogContentText'; // Not used in final modal
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton'; // Keep this
import CloseIcon from '@mui/icons-material/Close';
import BotManagementPage from './admin/BotManagementPage';
// --- NEW IMPORTS for Hamburger Menu ---
import MenuIcon from '@mui/icons-material/Menu';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Badge from '@mui/material/Badge'; // <-- Import Badge for unread count (optional)
import TwitterIcon from '@mui/icons-material/Twitter'; // <-- Import TwitterIcon
import OfferFeedbackPage from './admin/OfferFeedbackPage';

// --- AuthDisplay Component ---
function AuthDisplay() {
  const { user, login, logout, loading } = useAuth();
  const navigate = useNavigate();

  // --- State for Mobile Menu ---
  const [anchorEl, setAnchorEl] = React.useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // --- Handlers for Menu Items ---
  const handleNavigate = (path) => {
    navigate(path);
    handleMenuClose();
  };

  const handleLogoutAndClose = () => {
    logout();
    handleMenuClose();
  };
  // --- End Menu State & Handlers ---

  if (loading) {
    return <CircularProgress size={24} color="secondary" />;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      {user && user.isAuthenticated ? (
        // --- Logged-in user view ---
        <>
          {/* --- Desktop View (md and up) --- */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1.5 }}>
            {/* Admin Panel Link */}
            {/* {user.isAdmin && (
              <Button
                color="warning"
                variant="contained"
                component={RouterLink}
                to="/admin"
                size="small"
                startIcon={<AdminPanelSettingsIcon />}
                sx={{ mr: 1 }}
              >
                Admin
              </Button>
            )} */}
            {/* Messages Button */}
            <Button
              color="inherit"
              variant="outlined"
              component={RouterLink}
              to="/messages"
              size="small"
            >
              <MailOutlineIcon sx={{ mr: 0.5 }} /> Messages {/* TODO: Add unread count badge */}
            </Button>
            {/* Earn Spins Button */}
            <Button
              variant="contained"
              color="secondary"
              size="small"
              component={RouterLink}
              to="/offers"
              startIcon={<AddCircleOutlineIcon />}
              sx={{ mr: 1 }}
            >
              Earn spins
            </Button>
            {/* Spin Count Chip */}
            <Chip
              icon={<ConfirmationNumberIcon />}
              label={user?.spinCount ?? 0}
              color="secondary"
              size="medium"
            />
            {/* Inventory Button */}
            <Button
              color="inherit"
              variant="outlined"
              component={RouterLink}
              to="/inventory"
              size="small"
              startIcon={<InventoryIcon />}
              sx={{ ml: 1 }}
            >
              Inventory
            </Button>
            {/* User Avatar & Name */}
            {user.avatarUrl && (
              <Link component={RouterLink} to="/profile" sx={{ display: 'flex' }}>
                <Avatar
                  alt={user.displayName}
                  src={user.avatarUrl}
                  sx={{ width: 36, height: 36, border: theme => `2px solid ${theme.palette.primary.light}` }}
                />
              </Link>
            )}
            <Link
              component={RouterLink}
              to="/profile"
              color="text.primary" // Use text primary for better visibility on AppBar
              underline="hover"
              sx={{
                fontWeight: 'bold',
                '&:hover': {
                  color: 'primary.light'
                }
              }}
            >
              <Typography variant="body1"> {/* Adjusted variant */}
                {user.displayName}
              </Typography>
            </Link>
            {/* Logout Button */}
            <Button
              color="inherit"
              variant="outlined"
              onClick={logout}
              sx={{ ml: 1 }}
            >
              Logout
            </Button>
          </Box>

          {/* --- Mobile View (xs and sm) --- */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
            {/* Spin Count Chip (Optional: Keep visible on mobile) */}
            <Chip
              icon={<ConfirmationNumberIcon />}
              label={user?.spinCount ?? 0}
              color="secondary"
              size="small" // Smaller size for mobile
              sx={{ mr: 1 }}
            />
            {/* Hamburger Menu Button */}
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            {/* Mobile Menu */}
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={isMenuOpen}
              onClose={handleMenuClose}
              sx={{ mt: 1 }} // Add some margin top
            >
              {/* Menu Items */}
              <MenuItem onClick={() => handleNavigate('/profile')}>
                <Avatar src={user.avatarUrl} sx={{ width: 24, height: 24, mr: 1.5 }} /> Profile ({user.displayName})
              </MenuItem>
              <MenuItem onClick={() => handleNavigate('/inventory')}>
                <InventoryIcon sx={{ mr: 1.5 }} /> Inventory
              </MenuItem>
              <MenuItem onClick={() => handleNavigate('/messages')}>
                <MailOutlineIcon sx={{ mr: 1.5 }} /> Messages {/* TODO: Add unread count badge */}
              </MenuItem>
              <MenuItem onClick={() => handleNavigate('/offers')}>
                <AddCircleOutlineIcon sx={{ mr: 1.5 }} /> Earn Spins
              </MenuItem>
              {/* {user.isAdmin && (
                <MenuItem onClick={() => handleNavigate('/admin')}>
                  <AdminPanelSettingsIcon sx={{ mr: 1.5 }} /> Admin Panel
                </MenuItem>
              )} */}
              <Divider sx={{ my: 0.5 }} />
              <MenuItem onClick={handleLogoutAndClose}>Logout</MenuItem>
            </Menu>
          </Box>
        </>
        // --- END Logged-in user view ---
      ) : (
        // --- Logged-out user view ---
        <>
          <Button
            variant="contained"
            color="primary"
            onClick={login} // Directly call login
          >
            Login with Steam
          </Button>

          {/* Login modal removed */}

        </>
        // --- END Logged-out user view ---
      )}
    </Box>
  );
}

// --- AuthCallbackHandler Component ---
function AuthCallbackHandler() {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    refreshUser().then(() => {
      navigate('/'); // Redirect to home/spin page after successful login and refresh
    }).catch(err => {
      console.error("Failed to refresh user after callback:", err);
      navigate('/'); // Redirect home even on error
    });
  }, [refreshUser, navigate]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
      <CircularProgress color="primary" />
      <Typography variant="h6" sx={{ ml: 2 }}>Processing login...</Typography>
    </Box>
  );
}

// --- Footer Component ---
function Footer() {
  return (
    <Box
      component="footer"
      sx={{
        py: 3,
        px: 2,
        mt: 'auto',
        backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.7),
        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        backdropFilter: 'blur(4px)',
      }}
    >
      <Container maxWidth="lg">
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1, sm: 2, md: 4 }}
          justifyContent="center"
          alignItems="center"
          divider={<Divider orientation="vertical" flexItem sx={{ borderColor: theme => alpha(theme.palette.divider, 0.5) }} />}
        >
          <Link component={RouterLink} to="/terms" color="text.secondary" underline="hover" sx={{ '&:hover': { color: 'primary.light' } }}>
            Terms of Use
          </Link>
          <Link component={RouterLink} to="/privacy" color="text.secondary" underline="hover" sx={{ '&:hover': { color: 'primary.light' } }}>
            Privacy Policy
          </Link>
          <Link component={RouterLink} to="/faq" color="text.secondary" underline="hover" sx={{ '&:hover': { color: 'primary.light' } }}>
            FAQ
          </Link>
        </Stack>
        {/* X (Twitter) Link on a new line */}
        <Box sx={{ width: '100%', textAlign: 'center', mt: 1 }}> {/* Use Box to force new line and center */}
          <Link
            href="https://x.com/SpinnerCS"
            color="text.secondary"
            underline="hover"
            target="_blank" // Open in new tab
            rel="noopener noreferrer" // Security best practice
            sx={{ '&:hover': { color: 'primary.light' }, display: 'inline-flex', alignItems: 'center' }} // Use inline-flex to align icon and text
          >
            <TwitterIcon sx={{ mr: 0.5 }} /> X (Twitter)
          </Link>
        </Box>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
          {'© '}
          {new Date().getFullYear()}
          {' SpinnerCS. All rights reserved.'}
        </Typography>
        <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 1 }}>
          This website is not affiliated with Valve Corporation. Counter-Strike is a trademark or registered trademark of Valve Corporation.
        </Typography>
      </Container>
    </Box>
  );
}


// --- AppContent Component ---
function AppContent() {
  const { user, loading } = useAuth();

  // Show a global loading indicator only during the initial auth check
  if (loading && user === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: 'background.default' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      // bgcolor: 'background.default', // Remove or keep as fallback
      backgroundImage: 'url(/bg.webp)', // Path relative to the public folder
      backgroundSize: 'cover',        // Cover the entire viewport
      backgroundPosition: 'center center', // Center the image
      backgroundAttachment: 'fixed',   // Keep the background fixed during scroll
    }}>
      <Router>
        <AppBar position="static" sx={{ py: 0.5 }}>
          <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
            <Link component={RouterLink} to="/" sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
              <Box
                component="img"
                sx={{
                  height: { xs: 40, sm: 45 },
                  width: 'auto',
                  filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.4))',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.05)'
                  }
                }}
                alt="Spinner Site Logo"
                src="/logo_compressed.webp"
              />
            </Link>
            <Box sx={{ flexGrow: 1 }} />
            {/* AuthDisplay now contains the Dialog, controlled by context */}
            <AuthDisplay />
          </Toolbar>
        </AppBar>

        <Container component="main" sx={{ mt: { xs: 3, sm: 4 }, mb: 4, flexGrow: 1, px: { xs: 1, sm: 2 } }}>
          <Routes>
            {/* Conditional Landing/Spin Page */}
            <Route
              path="/"
              element={user?.isAuthenticated ? <SpinPage /> : <LandingPage />}
            />
            {/* Public Routes */}
            <Route path="/offers" element={<OffersPage />} />
            <Route path="/terms" element={<TermsOfUsePage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/referral/:referralCode" element={<ReferralHandler />} /> {/* <-- Add referral route */}
            <Route path="/auth/steam/callback" element={<AuthCallbackHandler />} />

            {/* Authenticated User Routes */}
            {/* These could be wrapped in another protected route component if needed */}
            {/* but for now, individual components handle the !user?.isAuthenticated case */}
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/inventory" element={<InventoryPage />} />
            <Route path="/messages" element={<MessagesPage />} /> {/* <-- Add Messages route */}

            {/* Admin Routes */}
            <Route path="/admin" element={<AdminRoute />}> {/* Protect all admin routes */}
              <Route element={<AdminLayout />}> {/* Use the admin layout */}
                {/* Default admin page (optional redirect or dashboard) */}
                <Route index element={<UserManagementPage />} /> {/* Example: Default to user management */}
                <Route path="users" element={<UserManagementPage />} />
                <Route path="items" element={<ItemManagementPage />} />
                <Route path="featured-items" element={<FeaturedItemManagementPage />} />
                <Route path="bots" element={<BotManagementPage />} />
                <Route path="offer-feedback" element={<OfferFeedbackPage />} />
                {/* Add more admin sub-routes here */}
              </Route>
            </Route>

            {/* Optional: Catch-all 404 Route */}
            {/* <Route path="*" element={<NotFoundPage />} /> */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Container>

        <Footer />
      </Router>
    </Box>
  );
}

// --- Main App Component ---
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
